import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { type PartialEntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { DateTime } from 'luxon';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import { projectConfig } from '../../config.js';
import { KPFPublicInformationAPI } from '../../public-access/api/KPFPublicInformationAPI.js';
import { chancenWerkMailer } from '../../server/mailer.js';
import { workTrackingSchukoEmail, workTrackingSingleCreatedEmail } from '../../server/mailTemplates.js';
import { WorkTrackingChange, WorkTrackingValDiff } from './WorkTrackingChange.js';
import { WorkTrackingEntry } from './WorkTrackingEntry.js';
import { combinedTrackingTaetigkeitenState, WorkTrackingTaetigkeitenState } from './WorkTrackingTaetigkeitenState.js';

export enum WorkTrackingTaetigkeitenTypes {
  LEKO = 'Lernkoordination',
  SCHUKO = 'Schulkoordination',
  INTENSIVE_COURSE = 'Intensivkursleitung',
  OS_COACH = 'OS-Coach',
  WORKSHOP = 'Workshop-Leitung',
}

export enum WorkTrackingSingleTaetigkeitenTypes {
  TEAM_MEETING = 'Teamtreffen',
  PREPARATION = 'Vorbereitung Lernförderung',
  POSTPARATION = 'Nachbereitung Lernförderung',
  OFFTIME_EVENT = 'Freizeitveranstaltung',
  AQUISE = 'SuS-Akquise',
  PUBLICITY_WORK = 'Öffentlichkeitsarbeit',
}

export function WorkTrackingTaetigkeitenTypeToKostenart(type: WorkTrackingTaetigkeitenTypes): string {
  switch (type) {
    case WorkTrackingTaetigkeitenTypes.LEKO:
      return '47810001';
    case WorkTrackingTaetigkeitenTypes.SCHUKO:
      return '47800001';
    case WorkTrackingTaetigkeitenTypes.INTENSIVE_COURSE:
      return '47820001';
    case WorkTrackingTaetigkeitenTypes.OS_COACH:
      return '47830001';
    case WorkTrackingTaetigkeitenTypes.WORKSHOP:
      return '47830001';
  }
}

/**
 * Entity to hold a work tracking taeigkeit like a "Lernkoordination". This can be generated from attendance.
 */
@Entity()
export class WorkTrackingTaetigkeit extends MongoEntity {
  static override _class = 'WorkTrackingTaetigkeit';
  static override _collectionName = 'work-tracking-taetigkeiten';

  public static NOT_GENERATED = 'NOT_GENERATED';

  constructor(doc: PartialEntityData<WorkTrackingTaetigkeit>) {
    super(doc);
  }

  static override async afterCreate(entity: WorkTrackingTaetigkeit, result: InsertOneResult<Document>) {
    await super.afterCreate(entity, result);

    if (entity.kpfID && entity.userID && entity.date) {
      await WorkTrackingEntry.addMentionedKPF(entity.userID, entity._id + '_' + entity.kpfID, entity.date);

      const user = (await loadMongoData(Mitarbeiter, entity.userID, {
        projection: {
          vorname: 1,
          nachname: 1,
        },
      })) as PartialEntityData<Mitarbeiter>;

      if (!user || (!user.vorname && !user.nachname)) {
        return;
      }

      if (
        entity.generatedFromID !== WorkTrackingTaetigkeit.NOT_GENERATED &&
        (entity.generatedFromID?.length ?? 0) > 0
      ) {
        // * Send SchuKo a notification
        const kpf = await KPF.getByID<KPF>(entity.kpfID)({ schuKos: { email: 1, emailChancenwerk: 1 } });
        try {
          const msg = await Message.compose({
            subject: `Tätigkeit für Monat ${entity.date.toFormat('MM')}. muss überprüft werden`,
            html: workTrackingSchukoEmail({
              name: `${user.vorname} ${user.nachname}`,
              date: entity.date,
              link: projectConfig.get('RAUM_URL') + 'work-tracking/' + entity._id,
            }),
          });

          await chancenWerkMailer.sendMail(
            msg,
            kpf?.schuKos?.map(s => new Recipient(s.emailChancenwerk ?? s.email)),
          );
        } catch (e) {
          logger.warn(e, 'Error while sending mail');
        }
      } else if (entity.createdByOwner) {
        // * Send internal operative (STB) a notification
        const kpf = await KPF.getByID<KPF>(entity.kpfID)({ personsOperativ: { email: 1, emailChancenwerk: 1 } });
        try {
          const msg = await Message.compose({
            subject: `Einzeltätigkeit für Monat ${entity.date.toFormat('MM')}. muss überprüft werden`,
            html: workTrackingSingleCreatedEmail({
              name: `${user.vorname} ${user.nachname}`,
              date: entity.date,
              link: projectConfig.get('RAUM_URL') + 'work-tracking/' + entity._id,
            }),
          });

          await chancenWerkMailer.sendMail(
            msg,
            kpf?.personsOperativ?.map(s => new Recipient(s.emailChancenwerk ?? s.email)),
          );
        } catch (e) {
          logger.warn(e, 'Error while sending mail');
        }
      }
    }
  }

  static override async afterUpdate(
    oldEntity: WorkTrackingTaetigkeit,
    newEntity: WorkTrackingTaetigkeit,
    result: UpdateResult,
  ) {
    await super.afterUpdate(oldEntity, newEntity, result);

    if (newEntity.kpfID && newEntity.userID && newEntity.date) {
      await WorkTrackingEntry.addMentionedKPF(newEntity.userID, newEntity._id + '_' + newEntity.kpfID, newEntity.date);
    } else if (oldEntity.kpfID && !newEntity.kpfID && oldEntity.userID && oldEntity.date) {
      await WorkTrackingEntry.removeMentionedKPF(
        oldEntity.userID,
        oldEntity._id + '_' + oldEntity.kpfID,
        oldEntity.date,
      );
    }
  }

  static override async afterRemove(id: string, entityData: EntityData<WorkTrackingTaetigkeit>): Promise<void> {
    super.afterRemove(id, entityData);

    if (entityData.kpfID && entityData.userID && entityData.date) {
      await WorkTrackingEntry.removeMentionedKPF(entityData.userID, id + '_' + entityData.kpfID, entityData.date);
    }
  }

  /**
   * If this was generated from an different entity like attendance, this is the ID of the attendance.
   * This is used to avoid duplicate entries.
   */
  @Field(type => String)
  generatedFromID: Maybe<string>;

  @Field(type => Boolean)
  isSingle: Maybe<boolean>;

  @DataLoadedField(type => Mitarbeiter, ({ userID }) => userID)
  user: Maybe<Mitarbeiter>;

  @validate(nonOptional())
  @Field(type => String)
  userID: Maybe<string>;

  @Field(type => String)
  stateContractor: Maybe<WorkTrackingTaetigkeitenState>;

  @Field(type => String)
  stateResponsible: Maybe<WorkTrackingTaetigkeitenState>;

  get state() {
    return combinedTrackingTaetigkeitenState([this.stateContractor, this.stateResponsible]);
  }

  @Field(type => Boolean)
  disabled: Maybe<boolean>;

  @Field(type => Boolean)
  createdByOwner: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => DateTime)
  date: Maybe<DateTime>;

  @Field(type => DateTime)
  startTime: Maybe<DateTime>;

  @Field(type => DateTime)
  endTime: Maybe<DateTime>;

  @DataLoadedField(type => KPF, ({ kpfID }) => kpfID)
  kpf: Maybe<KPF>;

  @Field(type => String, {
    resolve: function (this: WorkTrackingTaetigkeit) {
      return async () => {
        if (!this.kpfID) return null;
        const res = await KPFPublicInformationAPI.getByID([this.kpfID])({
          produkt: { kuerzel: 1 },
          school: { kuerzel: 1, name: 1 },
        });
        return res[0]?.school?.name;
      };
    },
  })
  kpfName: Maybe<string>;

  @Field(type => String)
  kpfID: Maybe<string>;

  @Field(type => String)
  type: Maybe<string>;

  @Field(type => Float)
  money: Maybe<number>;

  @Field(type => String)
  note: Maybe<string>;

  @Field(type => [WorkTrackingChange])
  changes: Maybe<WorkTrackingChange[]>;

  get lastNote() {
    if (!this.changes || this.changes.length === 0) {
      return '';
    }

    return this.changes[this.changes.length - 1]?.note ?? '';
  }

  static fieldNames(field: string) {
    return (
      {
        generatedFromID: 'generatedFromID',
        userID: 'NutzerID',
        stateContractor: 'Tätiger',
        stateResponsible: 'Verwaltung',
        date: 'Datum',
        disabled: 'Deaktiviert',
        startTime: 'Startzeit',
        endTime: 'Endzeit',
        kpfID: 'KPF',
        type: 'Art',
        money: '€',
        note: 'Notiz',
        changes: 'changes',
      }[field] ?? 'Unbekannt'
    );
  }

  /**
   * Check if the work tracking taeigkeit is the same as another and if not add the changes to the changes array.
   * @param userID User ID of the user who made the changes.
   * @param oldState The old state of the work tracking taeigkeit.
   * @param note Optional note to add to the changes.
   */
  addChanges(userID: string, oldState: WorkTrackingTaetigkeit, note?: Maybe<string>) {
    const diffs: WorkTrackingValDiff[] = [];

    if (this.disabled !== oldState.disabled) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'disabled',
          from: oldState.disabled ? 'Ja' : 'Nein',
          to: this.disabled ? 'Ja' : 'Nein',
        }),
      );
    }

    if (this.type !== oldState.type) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'type',
          from: oldState.type,
          to: this.type,
        }),
      );
    }

    if (this.money !== oldState.money) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'money',
          from: oldState.money?.toString(),
          to: this.money?.toString(),
        }),
      );
    }

    if (this.note !== oldState.note) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'note',
          from: oldState.note,
          to: this.note,
        }),
      );
    }

    if (oldState.startTime && !this.startTime?.equals(oldState.startTime)) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'startTime',
          from: oldState.startTime?.toISO(),
          to: this.startTime?.toISO(),
        }),
      );
    }

    if (oldState.endTime && !this.endTime?.equals(oldState.endTime)) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'endTime',
          from: oldState.endTime?.toISO(),
          to: this.endTime?.toISO(),
        }),
      );
    }

    if (oldState.date && !this.date?.equals(oldState.date)) {
      diffs.push(
        new WorkTrackingValDiff({
          val: 'date',
          from: oldState.date?.toISO(),
          to: this.date?.toISO(),
        }),
      );
    }

    if (diffs.length > 0 || note) {
      if (!this.changes) {
        this.changes = [];
      }

      if (this.userID === userID) {
        this.stateContractor = WorkTrackingTaetigkeitenState.FINISHED;
        this.stateResponsible = WorkTrackingTaetigkeitenState.QUESTION;
      } else {
        this.stateResponsible = WorkTrackingTaetigkeitenState.FINISHED;
        this.stateContractor = WorkTrackingTaetigkeitenState.QUESTION;
      }

      this.changes.push(
        new WorkTrackingChange({
          note: note ?? '',
          fromID: userID,
          date: DateTime.now().toJSDate(),
          diff: diffs,
        }),
      );
    }
  }

  /**
   * Change the state of the work tracking taeigkeit and add a change to the changes array.
   */
  changeState(userID: string, newState: WorkTrackingTaetigkeitenState, note?: string) {
    if (!this.changes) {
      this.changes = [];
    }

    // * Check if the state is already the same.
    if (this.userID === userID && this.stateContractor === newState) {
      return;
    }
    if (this.userID !== userID && this.stateResponsible === newState) {
      return;
    }

    // * Add the change to the changes array.
    this.changes.push(
      new WorkTrackingChange({
        fromID: userID,
        note,
        date: DateTime.now().toJSDate(),
        diff: [
          new WorkTrackingValDiff({
            val: this.userID === userID ? 'stateContractor' : 'stateResponsible',
            from: this.userID === userID ? this.stateContractor : this.stateResponsible,
            to: newState,
          }),
        ],
      }),
    );

    // * Change the state.
    if (userID === this.userID) {
      this.stateContractor = newState;
    } else {
      this.stateResponsible = newState;
    }
  }
}
